import type { LogRow } from './ability/context.js';
import { isRefusalCode } from './telemetry/refusal-code';

/** A boundary still commits; its unported effect becomes a GM-only log message. */
export interface UnportedSkipRow extends LogRow {
  roll_type_1: 'janus_unported_skip';
  roll_type_2: null;
  roll_result: 0;
  roll_private: 1;
  /** Plain reason text, persisted through the existing rolls_full column. */
  rolls_full: string;
}

let quietReasons: ReadonlySet<string> | null | undefined;
export const ENGINE_RELOAD_MESSAGE = 'A new FateTide version is available. Reload to continue making changes.';
/** Synchronous previews keep their verdict rows without publishing warnings. Recovery can mute only already-saved skip reasons. */
export function withQuietUnported<T>(run: () => T, reasons?: ReadonlySet<string>): T {
  const previous = quietReasons;
  quietReasons = previous === null || !reasons ? null : new Set([...(previous ?? []), ...reasons]);
  try { return run(); } finally { quietReasons = previous; }
}

function report(reason: string, detail: Record<string, unknown> | undefined, mode: 'refused' | 'skipped', telemetryReason = reason): void {
  if (quietReasons === null || mode === 'skipped' && quietReasons?.has(reason)) return;
  if (reason === 'engine-version-reload' && mode === 'refused' && typeof window !== 'undefined') {
    // Bundles and same-origin frames share the tab's terminal pause, rather than each spending a beacon budget.
    let owner: Window = window;
    try { if (window.top?.location.origin === window.location.origin) owner = window.top; } catch { /* Cross-origin embed owns its page. */ }
    const tab = owner as Window & { ftEngineReloadReported?: boolean };
    if (tab.ftEngineReloadReported) return;
    tab.ftEngineReloadReported = true;
  }
  console.warn('[janus-unported]', { reason, detail, mode });
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('ft:unported', { detail: { reason: telemetryReason, detail, mode } }));
  }
}

/** The version banner records the pause once; background guards never add a toast. */
export function noteEngineReload(detail?: Record<string, unknown>): void {
  report('engine-version-reload', detail, 'refused');
}

/** Player actions without a browser path stop here, before any dice or network request. A separate telemetry code preserves the player words and return value. */
export function refuseUnported(reason: string, detail?: Record<string, unknown>, playerMessage?: string, telemetryReason = reason): { kind: 'refused'; reason: string } {
  report(reason, detail, 'refused', isRefusalCode(reason) ? reason : telemetryReason);
  if (quietReasons !== null && typeof window !== 'undefined') {
    const warning = (window as unknown as { displayWarning?: (message: string) => void }).displayWarning;
    if (typeof warning === 'function') {
      // Keep the diagnostic reason stable while allowing an expected refusal to explain itself to the player.
      const message = reason === 'engine-version-reload' ? ENGINE_RELOAD_MESSAGE : playerMessage ?? 'Not yet supported in the browser: ' + reason;
      warning(message.replace(/[&<>"']/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch]!));
    }
  }
  return { kind: 'refused', reason };
}

// Shared footer bootstrap also serves inline campaign and item-page action handlers.
if (typeof window !== 'undefined') Object.assign(window, { ftRefuseUnported: refuseUnported });

/** Append this row to the boundary verdict; this primitive never posts or stalls the boundary. */
export function skipUnported(reason: string, detail?: Record<string, unknown>): UnportedSkipRow {
  report(reason, detail, 'skipped');
  return unportedSkipRow(reason);
}

/** Dry previews carry the same save shape without publishing an event. */
export function unportedSkipRow(reason: string): UnportedSkipRow {
  return {
    roll_type_1: 'janus_unported_skip',
    roll_type_2: null,
    roll_result: 0,
    roll_private: 1,
    roll_item_id: null,
    roll_by: null,
    roll_to: null,
    rolls_full: reason,
  };
}
